import "./App.css";
import React from "react";
import { ThemeProvider } from "./components/theme-provider";
import { BrowserRouter as Router, Switch, Route, useLocation } from "react-router-dom";
import QuickSocialUploader from "./pages/QuickSocialUploader";
import LandingPage from "./pages/Landing";
import PricingPage from "./pages/Pricing";
import TermsPage from "./pages/Terms";
import PrivacyPage from "./pages/Privacy";
import Settings from "./pages/Settings";
import MyMedia from "./pages/MyMedia";
import Billing from "./pages/Billing";
import Projects from "./pages/Projects";
import SocialConnections from "./pages/SocialConnections";
import Notifications from "./pages/Notifications";
import AdminPage from "./pages/Admin";
import UserDetail from "./pages/Admin/UserDetail";
import PostsPage from "./pages/Posts";
import Login from "./pages/Login";
import ForgotPassword from "./pages/Login/ForgotPassword";
import ResetPassword from "./pages/Login/ResetPassword";
import VerifyEmail from "./pages/Login/VerifyEmail";
import AuthSuccess from "./pages/Login/AuthSuccess";
import BillingStatus from "./pages/BillingStatus";
import SupportPage from "./pages/Support";
import Navbar from "./components/Navbar";
// import { CancelledSubscriptionBanner } from "./components/CancelledSubscriptionBanner";
import { Redirect } from "react-router-dom";
import { ProjectProvider } from "./contexts/ProjectContext";
import { PostModalProvider } from "./contexts/PostModalContext";
import { SupportProvider } from "./contexts/SupportContext";
import PostModal from "./components/PostModal/index";
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from "./components/ui/sonner";
import axios from "axios";
import { useGlobalState } from "./contexts/GlobalStateContext";
import MaintenanceOverlay from "./components/MaintenanceOverlay";
import GDPRConsentModal from "./components/GDPRConsentModal";
import Footer from "./components/Footer";
import { API_BASE_URL } from "./config";
import { isUserInEU } from "./utils/gdprDetection";
import { brand } from "./config/brand";

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5000,
      refetchOnWindowFocus: false,
    },
  },
});

// Auth Guard Component
const PrivateRoute = ({ children, ...rest }: any) => {
  const token = localStorage.getItem('token');
  return (
    <Route
      {...rest}
      render={({ location }) =>
        token ? (
          children
        ) : (
          <Redirect
            to={{
              pathname: "/login",
              state: { from: location }
            }}
          />
        )
      }
    />
  );
};

// Admin Guard Component
const AdminRoute = ({ children, ...rest }: any) => {
  const token = localStorage.getItem('token');
  const { user } = useGlobalState();

  return (
    <Route
      {...rest}
      render={({ location }) =>
        token && user?.isAdmin ? (
          children
        ) : (
          <Redirect
            to={{
              pathname: "/dashboard",
              state: { from: location }
            }}
          />
        )
      }
    />
  );
};

function App(): JSX.Element {
  const token = localStorage.getItem('token');
  const { isServerOffline, setIsServerOffline, setUser, user } = useGlobalState();
  const [showGDPRModal, setShowGDPRModal] = React.useState(false);
  const [checkingGDPR, setCheckingGDPR] = React.useState(true);

  // Setup Global Axios Interceptor for connection errors
  React.useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => {
        if (isServerOffline) setIsServerOffline(false);
        return response;
      },
      (error) => {
        if (error.response && error.response.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setUser(null);

          const publicPaths = ['/login', '/forgot-password', '/reset-password', '/verify-email', '/auth-success', '/pricing', '/terms', '/privacy', '/', '/support', '/support/ticket'];
          if (!publicPaths.includes(window.location.pathname)) {
            window.location.href = '/login';
          }
        }

        if (!error.response && error.code !== 'ERR_CANCELED') {
          setIsServerOffline(true);
        }
        return Promise.reject(error);
      }
    );

    const checkServerAndUser = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setIsServerOffline(false);
        return;
      }

      try {
        const response = await axios.get(`${API_BASE_URL}/auth/me`, {
          timeout: 5000,
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsServerOffline(false);
        if (response.data.user) {
          setUser(response.data.user);
        }
      } catch (err: any) {
        if (err.response && err.response.status === 401) {
          setIsServerOffline(false);
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setUser(null);

          const publicPaths = ['/login', '/forgot-password', '/reset-password', '/verify-email', '/auth-success', '/pricing', '/terms', '/privacy', '/', '/support', '/support/ticket'];
          if (!publicPaths.includes(window.location.pathname)) {
            window.location.href = '/login';
          }
          return;
        }
        if (!err.response) {
          setIsServerOffline(true);
        }
      }
    };

    checkServerAndUser();

    return () => axios.interceptors.response.eject(interceptor);
  }, [isServerOffline, setIsServerOffline, setUser]);

  // Check GDPR consent requirement
  React.useEffect(() => {
    const checkGDPRConsent = async () => {
      setCheckingGDPR(true);

      try {
        const storedConsent = localStorage.getItem('gdpr-consent');
        if (storedConsent) {
          const consent = JSON.parse(storedConsent);
          if (consent.accepted) {
            setCheckingGDPR(false);
            return;
          }
        }

        if (token && user) {
          if ((user as any).gdprConsent === true) {
            setCheckingGDPR(false);
            return;
          }
        }

        const inEU = await isUserInEU();
        if (inEU) {
          setShowGDPRModal(true);
        }
      } catch (error) {
        console.error('[App] Error checking GDPR consent:', error);
      } finally {
        setCheckingGDPR(false);
      }
    };

    checkGDPRConsent();
  }, [token, user, setUser]);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme={brand.defaultTheme} storageKey={brand.themeStorageKey}>
        <SupportProvider>
          <ProjectProvider>
            <PostModalProvider>
              <Router>
                <ScrollToTop />
                <div className="App" style={{ minHeight: '100vh' }}>
                  {isServerOffline && <MaintenanceOverlay />}
                  <Route
                    render={({ location }) => {
                      const publicPaths = ["/", "/pricing", "/terms", "/privacy", "/support", "/support/ticket"];
                      const isPublic = publicPaths.includes(location.pathname);
                      const token = localStorage.getItem('token');
                      if (token && !isPublic) {
                        return (
                          <>
                            <Navbar />
                            {/* <CancelledSubscriptionBanner /> */}
                          </>
                        );
                      }
                      return null;
                    }}
                  />
                  <Switch>
                    <Route exact path="/">
                      <LandingPage />
                    </Route>
                    <Route path="/pricing">
                      <PricingPage />
                    </Route>
                    <Route path="/terms">
                      <TermsPage />
                    </Route>
                    <Route path="/privacy">
                      <PrivacyPage />
                    </Route>
                    <Route path="/support">
                      <SupportPage />
                    </Route>
                    <Route path="/login">
                      <Login />
                    </Route>
                    <Route path="/forgot-password">
                      <ForgotPassword />
                    </Route>
                    <Route path="/reset-password">
                      <ResetPassword />
                    </Route>
                    <Route path="/auth-success">
                      <AuthSuccess />
                    </Route>
                    <Route path="/verify-email">
                      <VerifyEmail />
                    </Route>
                    <PrivateRoute path="/dashboard">
                      <QuickSocialUploader />
                    </PrivateRoute>
                    <PrivateRoute path="/settings">
                      <Settings />
                    </PrivateRoute>
                    <PrivateRoute path="/my-media/:mediaId">
                      <MyMedia />
                    </PrivateRoute>
                    <PrivateRoute path="/my-media">
                      <MyMedia />
                    </PrivateRoute>
                    <PrivateRoute path="/posts">
                      <PostsPage />
                    </PrivateRoute>
                    <PrivateRoute path="/billing">
                      <Billing />
                    </PrivateRoute>
                    <PrivateRoute path="/billing-status">
                      <BillingStatus />
                    </PrivateRoute>
                    {brand.features.showProjects && (
                    <PrivateRoute path="/projects">
                      <Projects />
                    </PrivateRoute>
                    )}
                    <PrivateRoute path="/social-connections">
                      <SocialConnections />
                    </PrivateRoute>
                    <PrivateRoute path="/notifications">
                      <Notifications />
                    </PrivateRoute>
                    <AdminRoute path="/admin/users/:userId">
                      <UserDetail />
                    </AdminRoute>
                    <AdminRoute path="/admin">
                      <AdminPage />
                    </AdminRoute>
                    <PrivateRoute path="/posts/:id">
                      <QuickSocialUploader />
                    </PrivateRoute>
                    <PrivateRoute path="/media/:id">
                      <QuickSocialUploader />
                    </PrivateRoute>
                    <Route path="*">
                      <Redirect to="/dashboard" />
                    </Route>
                  </Switch>
                  <Footer />
                  <PostModal />
                  <GDPRConsentModal
                    isOpen={showGDPRModal && !checkingGDPR}
                    onAccept={() => {
                      setShowGDPRModal(false);
                      if (token) {
                        axios.get(`${API_BASE_URL}/auth/me`, {
                          headers: { Authorization: `Bearer ${token}` }
                        }).then(response => {
                          if (response.data.user) {
                            setUser(response.data.user);
                          }
                        }).catch(console.error);
                      }
                    }}
                  />
                  <Toaster richColors position="top-right" offset={80} />
                </div>
              </Router>
            </PostModalProvider>
          </ProjectProvider>
        </SupportProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
